<template>
  <main class="points-task-page">
    <AppNavBar :title="t('task.title')" />

    <section class="points-task-page__content">
      <section class="task-summary">
        <h2>{{ t('task.howToGetPoints') }}</h2>
        <div class="task-summary__right">
          <span>{{ t('task.myPoints') }}</span>
          <div data-strong>{{ pointsBalance }}</div>
        </div>
      </section>

      <section class="task-section">
        <div class="task-section__head">
          <div class="task-section__title">
            <div data-strong>{{ t('task.dailyTasks') }}</div>
            <span>{{ t('task.checkInTotal') }}</span>
            <em>{{ checkInTotalCount }}</em>
            <span>{{ t('task.days') }}</span>
          </div>
          <button type="button" class="task-link" @click="openRules">
            {{ t('task.checkInRules') }}
          </button>
        </div>

        <div class="checkin-week">
          <button
            v-for="item in weekList"
            :key="`${item.weekCode}-${item.amount}`"
            type="button"
            class="checkin-day"
            :class="checkinDayClass(item)"
            :disabled="!canTapCheckin(item)"
            @click="handleCheckin(item)"
          >
            <template v-if="Number(item?.isCheckIn) === 1">
              <div class="checkin-day__top checkin-day__top--checked">
                <span>+{{ item.amount }}</span>
                <app-image src="@/assets/images/img_task_int.png" alt="" />
              </div>
              <div class="checkin-day__circle checkin-day__circle--checked">
                <van-icon name="success" />
              </div>
            </template>
            <template v-else>
              <div class="checkin-day__top" />
              <div class="checkin-day__circle" :class="{ 'checkin-day__circle--todo': canTapCheckin(item) }">
                <span>+{{ item.amount }}</span>
              </div>
            </template>
            <span class="checkin-day__label">{{ weekLabel(item.weekCode) }}</span>
          </button>
        </div>
      </section>

      <section class="task-section">
        <div class="task-section__head">
          <div class="task-section__title">
            <div data-strong>{{ t('task.routineTasks') }}</div>
          </div>
          <span class="task-section__hint">{{ t('task.dailyPointsLimit') }}</span>
        </div>

        <div class="task-cards">
          <article v-for="item in routineTasks" :key="item.title" class="task-card">
            <div class="task-card__head">
              <div class="task-card__title">
                <app-image :src="item.icon" alt="" />
                <div data-strong>{{ item.title }}</div>
                <van-icon name="info-o" />
              </div>
              <div class="task-card__reward">
                <span>{{ item.subTitle }}</span>
                <em>{{ item.reward }}</em>
              </div>
            </div>

            <div class="task-card__foot">
              <div class="task-card__progress">
                <van-progress :percentage="item.progress" stroke-width="4" color="#f4c31f" track-color="#f5f5f5" :show-pivot="false" />
                <span>{{ item.progressText }}</span>
              </div>
              <van-button round color="#ffe53e" class="task-card__action">
                {{ t('task.goFollow') }}
                <van-icon name="arrow" />
              </van-button>
            </div>
          </article>
        </div>
      </section>

      <section class="task-section">
        <div class="task-section__head">
          <div class="task-section__title">
            <div data-strong>{{ t('task.basicGrowth') }}</div>
          </div>
        </div>

        <article class="growth-card">
          <div class="growth-card__left">
            <app-image src="@/assets/images/task_cm.png" alt="" />
            <div>
              <div data-strong>{{ t('task.completeSignature') }}</div>
              <div class="growth-card__sub">
                <span>{{ t('task.signatureDone') }}</span>
                <em>{{ t('task.pointsPlusOne') }}</em>
              </div>
            </div>
          </div>
          <van-button round plain color="#d4ab06" class="growth-card__action">
            {{ t('task.goFollow') }}
            <van-icon name="arrow" />
          </van-button>
        </article>
      </section>
    </section>
  </main>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { showConfirmDialog, showFailToast, showSuccessToast, showToast } from 'vant';
import { useI18n } from 'vue-i18n';
import AppNavBar from '@/components/AppNavBar.vue';
import { useProfileStore } from '@/stores/profile';
import { usePointsTaskStore } from '@/stores/points-task';

const { t } = useI18n();
const profileStore = useProfileStore();
const pointsTaskStore = usePointsTaskStore();

const pointsBalance = computed(() => pointsTaskStore.pointsBalance);
const checkInTotalCount = computed(() => pointsTaskStore.checkInTotalCount);
const weekList = computed(() => pointsTaskStore.weekList);

const routineTasks = computed(() => [
  {
    icon: new URL('@/assets/images/task_1.png', import.meta.url).href,
    title: t('task.postMoments'),
    subTitle: t('task.perPost'),
    reward: t('task.pointsPlusOne'),
    progress: 20,
    progressText: '1/20'
  },
  {
    icon: new URL('@/assets/images/task_1.png', import.meta.url).href,
    title: t('task.postMoments'),
    subTitle: t('task.perPost'),
    reward: t('task.pointsPlusOne'),
    progress: 20,
    progressText: '1/20'
  }
]);

function weekLabel(weekCode) {
  const map = [
    t('task.monday'),
    t('task.tuesday'),
    t('task.wednesday'),
    t('task.thursday'),
    t('task.friday'),
    t('task.saturday'),
    t('task.sunday')
  ];
  return map[Number(weekCode) || 0] || '--';
}

function checkinDayClass(item = {}) {
  if (Number(item?.status) === 0 && Number(item?.isCheckIn) === 0) {
    return 'checkin-day--disabled';
  }
  if (Number(item?.isCheckIn) === 1) {
    return 'checkin-day--checked';
  }
  return 'checkin-day--todo';
}

function canTapCheckin(item = {}) {
  return Number(item?.isCheckIn) === 0 && Number(item?.isToday) === 1;
}

async function openRules() {
  await showConfirmDialog({
    title: t('task.checkInRules'),
    message: `${t('task.checkInRuleOne')}\n\n${t('task.checkInRuleTwo')}`,
    showCancelButton: false,
    confirmButtonText: t('common.confirm'),
    messageAlign: 'left'
  });
}

async function loadTaskInfo() {
  await pointsTaskStore.fetch(profileStore.platformAddress || null);
}

async function handleCheckin(item) {
  if (!canTapCheckin(item)) {
    return;
  }
  if (!profileStore.platformAddress) {
    showToast(t('auth.login'));
    return;
  }
  try {
    const result = await pointsTaskStore.checkIn(profileStore.platformAddress);
    showSuccessToast(t('mine.checkInSuccess', { reward: result?.reward || 0 }));
  } catch (error) {
    showFailToast(error?.message || t('common.retryLater'));
  }
}

onMounted(() => {
  loadTaskInfo();
});
</script>

<style scoped lang="scss">
.points-task-page {
  min-height: var(--app-viewport-height);
  background: #f7f7f7;
}

.points-task-page__content {
  padding: 0 14px 24px;
}

.task-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0;
  border-bottom: 1px solid #e5e5e5;
}

.task-summary h2 {
  margin: 0;
  color: #000;
  font-size: 20px;
  line-height: 28px;
  font-weight: 500;
}

.task-summary__right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.task-summary__right span {
  color: #666;
  font-size: 14px;
}

.task-summary__right [data-strong] {
  color: #d4ab06;
  font-size: 18px;
  font-weight: 700;
}

.task-section {
  margin-top: 14px;
}

.task-section__head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 10px;
}

.task-section__title {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  flex-wrap: wrap;
}

.task-section__title [data-strong] {
  color: #000;
  font-size: 16px;
  line-height: 22px;
  font-weight: 500;
}

.task-section__title span {
  color: #999;
  font-size: 14px;
}

.task-section__title em {
  color: #e62412;
  font-size: 15px;
  font-style: normal;
}

.task-link,
.task-section__hint {
  color: #666;
  font-size: 12px;
}

.task-link {
  padding: 0;
  border: 0;
  background: transparent;
}

.checkin-week {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
}

.checkin-day {
  padding: 4px;
  border: 0;
  border-radius: 6px;
  background: #ffffe3;
}

.checkin-day--disabled {
  background: #f0f0f0;
}

.checkin-day--checked {
  background: #fad414;
}

.checkin-day__top {
  height: 20px;
}

.checkin-day__top--checked {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  color: #fff;
  font-size: 12px;
}

.checkin-day__top--checked .app-image {
  width: 16px;
  height: 16px;
}

.checkin-day__circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  margin: 0 auto;
  border: 3px solid #e5e5e5;
  border-radius: 999px;
  background: #f7f7f7;
  color: #bfbfbf;
  font-size: 12px;
}

.checkin-day__circle--todo {
  border-color: #fad414;
  background: #fff88f;
  color: #ad8500;
}

.checkin-day__circle--checked {
  border-color: transparent;
  background: transparent;
  color: #fff;
  font-size: 24px;
}

.checkin-day__label {
  display: block;
  margin-top: 4px;
  color: #946c00;
  font-size: 14px;
  text-align: center;
}

.checkin-day--disabled .checkin-day__label {
  color: #8c8c8c;
}

.task-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.task-card,
.growth-card {
  padding: 12px;
  border-radius: 8px;
  background: #fff;
}

.task-card__title {
  display: flex;
  align-items: center;
  gap: 4px;
}

.task-card__title .app-image {
  width: 20px;
  height: 20px;
}

.task-card__title [data-strong] {
  color: #000;
  font-size: 15px;
  font-weight: 700;
}

.task-card__title :deep(.van-icon) {
  color: #999;
  font-size: 16px;
}

.task-card__reward {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
}

.task-card__reward span {
  color: #999;
  font-size: 13px;
}

.task-card__reward em {
  color: #d4ab06;
  font-style: normal;
  font-size: 14px;
}

.task-card__foot {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-top: 38px;
  gap: 10px;
}

.task-card__progress {
  min-width: 80px;
}

.task-card__progress span {
  display: block;
  margin-top: 4px;
  color: rgba(0, 0, 0, 0.55);
  font-size: 14px;
}

.task-card__action,
.growth-card__action {
  flex-shrink: 0;
  min-width: 92px;
  height: 34px;
  color: #000;
  font-size: 14px;
  border: 0;
}

.growth-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.growth-card__left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.growth-card__left .app-image {
  width: 50px;
  height: 50px;
}

.growth-card__left [data-strong] {
  color: #000;
  font-size: 16px;
  font-weight: 500;
}

.growth-card__sub {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 2px;
}

.growth-card__sub span {
  color: rgba(0, 0, 0, 0.35);
  font-size: 13px;
}

.growth-card__sub em {
  color: #d4ab06;
  font-style: normal;
  font-size: 14px;
}
</style>
